import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { fetchGamify } from "@/lib/api";
import { Flame, BookOpen, CircleDot, Award, Sparkles, TrendingUp, MessageSquare, Users, ArrowRight } from "lucide-react";

const Stat = ({ icon: Icon, value, label, testid }) => (
  <div data-testid={testid} className="border border-line bg-surface p-5">
    <Icon className="h-5 w-5 text-gold" />
    <p className="mt-3 font-serif-latin text-3xl text-ink">{value}</p>
    <p className="text-xs uppercase tracking-wider text-ink-3">{label}</p>
  </div>
);

export default function Sadhana() {
  const { pick } = useLang();
  const { user } = useAuth();
  const [g, setG] = useState(null);

  useEffect(() => { if (user) fetchGamify().then(setG).catch(() => {}); }, [user]);

  if (!user) {
    return (
      <div data-testid="sadhana-page">
        <Seo path="/sadhana" title="My Sadhana — Your Daily Practice Dashboard" description="Track your japa, reading, daily pooja, streak and sadhana points — your personal spiritual practice dashboard." />
        <PageHero art={HERO_ART.devotion} overline={pick("साधना", "Practice")} deva="मेरी साधना" en={pick("मेरी साधना", "My Sadhana")}
          tagline={pick("जप, पाठ, दैनिक पूजा, निरंतरता एवं साधना-अंक — सब एक स्थान पर।", "Your japa, reading, daily pooja, streak and points — all in one place.")} />
        <section className="mx-auto max-w-2xl px-5 py-16 text-center md:px-8">
          <p className="text-ink-2">{pick("अपनी साधना संजोने हेतु लॉगिन करें।", "Sign in to begin tracking your sadhana.")}</p>
          <Link to="/login" className="mt-5 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white">{pick("लॉगिन करें", "Sign in")} <ArrowRight className="h-4 w-4" /></Link>
        </section>
      </div>
    );
  }

  const level = g?.level;
  const pct = level && level.next_threshold
    ? Math.min(100, Math.round(((g.points - level.current_threshold) / (level.next_threshold - level.current_threshold)) * 100))
    : 100;

  const actions = [
    { to: "/japa", deva: "जप", en: "Japa", icon: CircleDot },
    { to: "/dainik-pooja", deva: "दैनिक पूजा", en: "Daily Pooja", icon: Flame },
    { to: "/kathas", deva: "पठन", en: "Read", icon: BookOpen },
    { to: "/quiz", deva: "प्रश्नोत्तरी", en: "Quiz", icon: Sparkles },
  ];

  return (
    <div data-testid="sadhana-page">
      <Seo path="/sadhana" title="My Sadhana — Your Daily Practice Dashboard" description="Track your japa, reading, daily pooja, streak and sadhana points." />
      <PageHero art={HERO_ART.devotion} overline={pick("साधना", "Practice")} deva="मेरी साधना"
        en={pick(`नमस्ते, ${g?.name || ""}`, `Namaste, ${g?.name || "Sadhak"}`)}
        tagline={pick("आपकी नित्य साधना की झलक — निरंतर बने रहें।", "A glance at your daily practice — keep the flame steady.")} />

      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Level */}
        <div className="border border-line bg-surface p-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="overline-label text-gold">{pick("स्तर", "Level")} {level?.level}</p>
              <p className="font-deva text-2xl text-ink">{level?.deva} <span className="font-serif-latin text-lg text-ink-2">· {level?.en}</span></p>
            </div>
            <p className="font-serif-latin text-3xl text-saffron" data-testid="sadhana-points">{g?.points ?? 0} <span className="text-sm text-ink-3">{pick("अंक", "points")}</span></p>
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden bg-line">
            <div className="h-full bg-[var(--saffron)]" style={{ width: `${pct}%` }} />
          </div>
          {level?.next_en && <p className="mt-2 text-xs text-ink-3">{level.next_threshold - g.points} {pick("अंक शेष — अगला स्तर", "points to")} {level.next_en}</p>}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat testid="stat-streak" icon={TrendingUp} value={g?.streak ?? 0} label={pick("दिन निरंतर", "day streak")} />
          <Stat testid="stat-japa" icon={CircleDot} value={g?.japa_total ?? 0} label={pick("कुल जप", "total japa")} />
          <Stat testid="stat-pooja" icon={Flame} value={g?.pooja_days ?? 0} label={pick("पूजा दिवस", "pooja days")} />
          <Stat testid="stat-reads" icon={BookOpen} value={g?.reads ?? 0} label={pick("पठित", "read")} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat testid="stat-quiz" icon={Sparkles} value={g?.quiz_correct ?? 0} label={pick("प्रश्न सही", "quiz correct")} />
          <Stat testid="stat-reputation" icon={Award} value={g?.reputation ?? 0} label={pick("प्रतिष्ठा", "reputation")} />
          <Stat testid="stat-threads" icon={MessageSquare} value={g?.threads ?? 0} label={pick("चर्चाएँ", "discussions")} />
          <Stat testid="stat-answers" icon={Users} value={g?.answers ?? 0} label={pick("उत्तर", "answers")} />
        </div>

        {/* Quick actions */}
        <h2 className="mt-12 font-serif-latin text-xl text-ink">{pick("आज की साधना", "Today's practice")}</h2>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          {actions.map((a) => (
            <Link key={a.to} to={a.to} data-testid={`sadhana-action-${a.to.slice(1)}`} className="group flex items-center gap-3 border border-line bg-surface p-4 transition-colors hover:border-[var(--saffron)]">
              <a.icon className="h-5 w-5 text-saffron" />
              <span className="font-deva-body text-ink">{pick(a.deva, a.en)}</span>
              <ArrowRight className="ml-auto h-4 w-4 text-ink-3 transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>

        {/* Badges */}
        {g?.all_badges && (
          <>
            <h2 className="mt-12 font-serif-latin text-xl text-ink">{pick("उपलब्धियाँ", "Badges")}</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {g.all_badges.map((b) => {
                const earned = (g.badges || []).some((e) => e.id === b.id);
                return (
                  <div key={b.id} data-testid={`badge-${b.id}`} className={`border p-4 ${earned ? "border-gold/50 bg-surface" : "border-line bg-surface opacity-45"}`}>
                    <Award className={`h-5 w-5 ${earned ? "text-gold" : "text-ink-3"}`} />
                    <p className="mt-2 font-deva text-ink">{b.deva}</p>
                    <p className="text-xs text-ink-2">{b.en}</p>
                    <p className="mt-1 text-[11px] text-ink-3">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/leaderboard" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("साधना सूची", "Sadhana Leaderboard")}</Link>
          <Link to="/namasmarana-groups" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("नामस्मरण समूह", "Namasmarana Groups")}</Link>
        </div>
      </section>
    </div>
  );
}
